import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid,
} from 'recharts';
import EloChart from '../components/EloChart';
import './Statistics.css';

/* ─────────────────────────────────────────────────────────────
   STATISTICS — in-depth performance analytics for the logged-in
   player. Filterable by timeframe (Lifetime / Year / Month / Week).
   All stats are computed client-side from a single confirmed-matches
   query plus small lifetime-only fetches for peak ELO and global rank.
   ───────────────────────────────────────────────────────────── */

const TIMEFRAMES = [
  { key: 'lifetime', label: 'Lifetime' },
  { key: 'year',     label: 'Last Year' },
  { key: 'month',    label: 'Last Month' },
  { key: 'week',     label: 'Last Week' },
];

function getStartDate(tf) {
  const d = new Date();
  if (tf === 'week')  { d.setDate(d.getDate() - 7);        return d; }
  if (tf === 'month') { d.setMonth(d.getMonth() - 1);       return d; }
  if (tf === 'year')  { d.setFullYear(d.getFullYear() - 1); return d; }
  return null; // lifetime — no filter
}

/* Returns the Monday of the week containing dateStr */
function getMondayOf(dateStr) {
  const d = new Date(dateStr);
  const day = d.getDay(); // 0 = Sun
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function weekLabel(dateStr) {
  const mon = getMondayOf(dateStr);
  return 'Wk ' + mon.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function buildActivityData(matches, timeframe) {
  if (timeframe === 'week') {
    const counts = [0, 0, 0, 0, 0, 0, 0];
    matches.forEach(m => { counts[new Date(m.played_at).getDay()]++; });
    return DAY_LABELS.map((label, i) => ({ label, count: counts[i] }));
  }
  // Group by calendar week
  const map = new Map();
  matches.forEach(m => {
    const mon = getMondayOf(m.played_at);
    const key = mon.toISOString();
    if (!map.has(key)) map.set(key, { label: weekLabel(m.played_at), mon, count: 0 });
    map.get(key).count++;
  });
  return Array.from(map.values())
    .sort((a, b) => a.mon - b.mon)
    .map(({ label, count }) => ({ label, count }));
}

/* ── Sub-components ─────────────────────────────────────────── */

function StatBlock({ label, value, sub, valueClass }) {
  return (
    <div className="stats-block">
      <span className={`stats-block-value${valueClass ? ` ${valueClass}` : ''}`}>{value}</span>
      <span className="stats-block-label">{label}</span>
      {sub && <span className="stats-block-sub">{sub}</span>}
    </div>
  );
}

function BracketRow({ label, wins, played }) {
  const pct = played > 0 ? Math.round((wins / played) * 100) : 0;
  return (
    <div className="stats-bracket-row">
      <span className="stats-bracket-label">{label}</span>
      <div className="stats-bracket-bar-wrap">
        <div className="stats-bracket-bar" style={{ width: played > 0 ? `${pct}%` : '0%' }} />
      </div>
      <span className="stats-bracket-pct">{played > 0 ? `${pct}%` : '—'}</span>
      <span className="stats-bracket-record">{played > 0 ? `${wins}W / ${played - wins}L` : 'No data'}</span>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────── */

function Statistics() {
  const { user } = useAuth();

  const [timeframe,  setTimeframe]  = useState('lifetime');
  const [loading,    setLoading]    = useState(true);
  const [profile,    setProfile]    = useState(null);
  const [matches,    setMatches]    = useState([]);   // normalized
  const [peakElo,    setPeakElo]    = useState(null);
  const [topPercent, setTopPercent] = useState(null);
  const [tickColor,  setTickColor]  = useState(() =>
    getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim()
  );

  // Re-read chart tick color whenever theme changes
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setTickColor(
        getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim()
      )
    );
    obs.observe(document.documentElement, {
      attributes: true, attributeFilter: ['data-theme', 'class'],
    });
    return () => obs.disconnect();
  }, []);

  // Fetch profile, peak ELO, and global percentile once (lifetime, not timeframe-filtered)
  useEffect(() => {
    if (!user) return;
    async function fetchGlobal() {
      const { data: prof } = await supabase
        .from('profiles')
        .select('id, username, elo_rating, wins, losses, matches_played, created_at')
        .eq('id', user.id)
        .single();
      setProfile(prof);

      // Peak ELO from full rating history
      const { data: hist } = await supabase
        .from('rating_history')
        .select('elo_rating')
        .eq('user_id', user.id)
        .eq('is_doubles', false);
      setPeakElo(hist && hist.length > 0
        ? Math.max(...hist.map(r => r.elo_rating))
        : (prof?.elo_rating ?? null));

      // Global percentile — how many rated players have a higher ELO?
      if (prof?.elo_rating) {
        const { count: total } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .not('elo_rating', 'is', null);
        const { count: above } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .gt('elo_rating', prof.elo_rating);
        if (total > 0) {
          setTopPercent(Math.max(1, Math.ceil(((above + 1) / total) * 100)));
        }
      }
    }
    fetchGlobal();
  }, [user]);

  // Re-fetch matches whenever timeframe changes
  useEffect(() => {
    if (!user) return;
    async function fetchMatches() {
      setLoading(true);
      const start = getStartDate(timeframe);

      let q = supabase
        .from('matches')
        .select(`
          id, winner_elo_change, loser_elo_change, played_at,
          winner:profiles!winner_id(id, elo_rating),
          loser:profiles!loser_id(id, elo_rating)
        `)
        .or(`winner_id.eq.${user.id},loser_id.eq.${user.id}`)
        .eq('status', 'confirmed')
        .order('played_at', { ascending: true });

      if (start) q = q.gte('played_at', start.toISOString());

      const { data: rows } = await q;

      // Normalize each match: flag win/loss, extract opponent and ELO Δ
      // Filter out matches where a player's profile was deleted (join returns null)
      const normalized = (rows || []).filter(m => m.winner && m.loser).map(m => {
        const isWin     = m.winner.id === user.id;
        const opponent  = isWin ? m.loser : m.winner;
        const eloChange = isWin ? (m.winner_elo_change ?? 0) : (m.loser_elo_change ?? 0);
        return { isWin, opponent, eloChange, played_at: m.played_at };
      });

      setMatches(normalized);
      setLoading(false);
    }
    fetchMatches();
  }, [user, timeframe]);

  // ── Derived stats ──────────────────────────────────────────
  const n      = matches.length;
  const wins   = matches.filter(m => m.isWin).length;
  const losses = n - wins;
  const winRate = n > 0 ? Math.round((wins / n) * 100) : 0;

  const rawNetElo = matches.reduce((s, m) => s + m.eloChange, 0);
  const netElo    = Math.round(rawNetElo);
  const netEloStr = n > 0 ? (netElo >= 0 ? `+${netElo}` : `${netElo}`) : '—';

  const avgEloRaw = n > 0 ? rawNetElo / n : null;
  const avgEloStr = avgEloRaw != null
    ? (avgEloRaw >= 0 ? `+${avgEloRaw.toFixed(1)}` : avgEloRaw.toFixed(1))
    : '—';

  // Longest win streak (only counts consecutive wins)
  let longestStreak = 0;
  let tempStreak    = 0;
  for (let i = 0; i < n; i++) {
    if (matches[i].isWin) {
      tempStreak++;
      longestStreak = Math.max(longestStreak, tempStreak);
    } else {
      tempStreak = 0;
    }
  }

  // Current streak (trailing consecutive same result)
  let currentStreak = 0;
  if (n > 0) {
    const lastIsWin = matches[n - 1].isWin;
    for (let i = n - 1; i >= 0 && matches[i].isWin === lastIsWin; i--) currentStreak++;
  }
  const streakIsWin      = n > 0 ? matches[n - 1].isWin : null;
  const currentStreakLabel = currentStreak > 0
    ? `Current ${streakIsWin ? 'Win' : 'Loss'} Streak`
    : 'Current Streak';
  const currentStreakClass = currentStreak > 0
    ? (streakIsWin ? 'stats-block-value-win' : 'stats-block-value-loss')
    : '';

  // Performance by opponent ELO bracket (vs current ELO as proxy)
  const userElo = profile?.elo_rating ?? 1000;
  const bkt = {
    higher:  { w: 0, p: 0 },
    similar: { w: 0, p: 0 },
    lower:   { w: 0, p: 0 },
  };
  matches.forEach(m => {
    const oppElo = m.opponent?.elo_rating ?? 1000;
    const key = oppElo > userElo + 50 ? 'higher' : oppElo < userElo - 50 ? 'lower' : 'similar';
    bkt[key].p++;
    if (m.isWin) bkt[key].w++;
  });

  // Activity chart data
  const activityData = buildActivityData(matches, timeframe);
  const hasActivity  = activityData.some(d => d.count > 0);

  return (
    <div className="stats-page">
      <div className="stats-inner">

        {/* Page header */}
        <div className="stats-header">
          <h1 className="stats-title">Performance Statistics</h1>
        </div>

        {/* Timeframe selector */}
        <div className="stats-timeframe-row">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf.key}
              className={`stats-tf-btn ${timeframe === tf.key ? 'active' : ''}`}
              onClick={() => setTimeframe(tf.key)}
            >
              {tf.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="stats-loading">
            <span className="stats-loading-star">★</span>
            <span className="stats-loading-star">★</span>
            <span className="stats-loading-star">★</span>
          </div>
        ) : (
          <>
            {/* ── Key Numbers ──────────────────────────────── */}
            <section className="stats-section">
              <h2 className="stats-section-title">Key Numbers</h2>
              <div className="stats-blocks-grid stats-blocks-5">
                <StatBlock label="Matches"     value={n > 0 ? n : '—'} />
                <StatBlock label="Win Rate"    value={n > 0 ? `${winRate}%` : '—'} />
                <StatBlock label="Peak ELO"    value={peakElo ?? '—'} />
                <StatBlock
                  label="Net ELO"
                  value={netEloStr}
                  sub={timeframe !== 'lifetime' ? 'this period' : undefined}
                />
                <StatBlock
                  label="Global Rank"
                  value={topPercent != null ? `Top ${topPercent}%` : '—'}
                />
              </div>
            </section>

            {/* ── Rating History ───────────────────────────── */}
            {profile && (
              <section className="stats-section">
                <h2 className="stats-section-title">Rating History</h2>
                <EloChart
                  userId={profile.id}
                  refreshKey={timeframe}
                  createdAt={profile.created_at}
                  startDate={getStartDate(timeframe)}
                />
              </section>
            )}

            {/* ── Win / Loss Bar ───────────────────────────── */}
            {n > 0 && (
              <section className="stats-section">
                <h2 className="stats-section-title">Win / Loss</h2>
                <div className="stats-winloss-card">
                  <div className="stats-winloss-bar">
                    <div className="stats-winloss-win"  style={{ width: `${winRate}%` }} />
                    <div className="stats-winloss-loss" style={{ width: `${100 - winRate}%` }} />
                  </div>
                  <div className="stats-winloss-labels">
                    <span className="stats-winloss-w-label">{wins}W · {winRate}%</span>
                    <span className="stats-winloss-l-label">{100 - winRate}% · {losses}L</span>
                  </div>
                </div>
              </section>
            )}

            {/* ── Streaks & Records ────────────────────────── */}
            <section className="stats-section">
              <h2 className="stats-section-title">Streaks & Records</h2>
              <div className="stats-blocks-grid stats-blocks-3">
                <StatBlock
                  label={currentStreakLabel}
                  value={currentStreak > 0 ? currentStreak : '—'}
                  valueClass={currentStreakClass}
                />
                <StatBlock label="Longest Win Streak" value={longestStreak > 0 ? longestStreak : '—'} />
                <StatBlock label="Avg ±ELO / Match"  value={avgEloStr} />
              </div>
            </section>

            {/* ── Performance vs. Opponent ─────────────────── */}
            <section className="stats-section">
              <h2 className="stats-section-title">Performance vs. Opponent</h2>
              <div className="stats-bracket-card">
                <BracketRow label="vs Higher Rated" wins={bkt.higher.w}  played={bkt.higher.p} />
                <BracketRow label="vs Similar"      wins={bkt.similar.w} played={bkt.similar.p} />
                <BracketRow label="vs Lower Rated"  wins={bkt.lower.w}   played={bkt.lower.p} />
              </div>
              <p className="stats-bracket-note">
                Higher = 50+ ELO above · Similar = within ±50 · Lower = 50+ ELO below
              </p>
            </section>

            {/* ── Activity Chart ───────────────────────────── */}
            <section className="stats-section">
              <h2 className="stats-section-title">Activity</h2>
              {hasActivity ? (
                <div className="stats-chart-card">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={activityData}
                      margin={{ top: 8, right: 8, bottom: 20, left: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--border)"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: tickColor, fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        interval={timeframe === 'week' ? 0 : 'preserveStartEnd'}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: tickColor, fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={24}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        contentStyle={{
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border)',
                          borderRadius: '10px',
                          fontSize: '13px',
                          color: 'var(--text-primary)',
                        }}
                        formatter={value => [value, 'Matches']}
                      />
                      <Bar dataKey="count" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="stats-empty-chart">No activity in this period</div>
              )}
            </section>

          </>
        )}

      </div>
    </div>
  );
}

export default Statistics;
